import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  computed,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatIcon } from '@angular/material/icon';
import { Subject, takeUntil } from 'rxjs';
import { FormBase } from 'src/app/_models/form-base';
import {
  LICENSE_LIMITS,
  LicenseDisplay,
  LicenseType,
  isLicenseType,
  licenseTypeOptions,
} from 'src/app/_models/license-type';
import { ModalService } from 'src/app/_services/modal.service';
import { ButtonComponent } from '../../buttons/button/button.component';
import { MatIconButtonComponent } from '../../buttons/mat-icon-button/mat-icon-button.component';
import { UnderlinedDropdownFormFieldComponent } from '../../forms/underlined-dropdown-form-field/underlined-dropdown-form-field.component';
import { LicenceCardComponent } from '../../cards/licence-card/licence-card.component';

@Component({
  selector: 'app-edit-licence-type-modal',
  templateUrl: './edit-licence-type-modal.component.html',
  styleUrl: './edit-licence-type-modal.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ReactiveFormsModule,
    UnderlinedDropdownFormFieldComponent,
    ButtonComponent,
    MatIconButtonComponent,
    MatIcon,
    LicenceCardComponent,
  ],
})
export class EditLicenceTypeModalComponent implements OnInit, OnDestroy {
  private readonly modalService = inject(ModalService);
  private readonly fb = inject(FormBuilder);
  private readonly elementRef = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly element: HTMLElement;
  private readonly unsubscribe$ = new Subject<void>();

  id = input<string>();
  currentLicenceType = input<LicenseType>();
  numberOfUsers = input<number>(0);
  numberOfDevices = input<number>(0);
  processing = input<boolean>(false);

  licenceTypeConfirmed = output<LicenseType>();

  selectedLicenceType = signal<LicenseType | null>(null);

  readonly selectedLimits = computed(() => {
    const type = this.selectedLicenceType();
    if (type === null) return null;
    return LICENSE_LIMITS[type];
  });

  readonly isUsersLimited = computed(() => {
    const limits = this.selectedLimits();
    return limits !== null && limits.maxUsers !== Infinity;
  });

  readonly isDevicesLimited = computed(() => {
    const limits = this.selectedLimits();
    return limits !== null && limits.maxDevices !== Infinity;
  });

  readonly isUsersOverLimit = computed(() => {
    if (!this.isUsersLimited()) return false;
    return this.numberOfUsers() > this.selectedLimits().maxUsers;
  });

  readonly isDevicesOverLimit = computed(() => {
    if (!this.isDevicesLimited()) return false;
    return this.numberOfDevices() > this.selectedLimits().maxDevices;
  });

  readonly isOverLimit = computed(
    () => this.isUsersOverLimit() || this.isDevicesOverLimit(),
  );

  readonly usersBarPercent = computed(() => {
    const limits = this.selectedLimits();
    if (!limits || limits.maxUsers === Infinity) return 0;
    return Math.min((this.numberOfUsers() / limits.maxUsers) * 100, 100);
  });

  readonly devicesBarPercent = computed(() => {
    const limits = this.selectedLimits();
    if (!limits || limits.maxDevices === Infinity) return 0;
    return Math.min((this.numberOfDevices() / limits.maxDevices) * 100, 100);
  });

  readonly overLimitWarningText = computed(() => {
    const overItems: string[] = [];
    if (this.isUsersOverLimit()) overItems.push('users');
    if (this.isDevicesOverLimit()) overItems.push('devices');
    if (overItems.length === 0) return '';
    const type = this.selectedLicenceType();
    const typeName = type !== null ? LicenseDisplay[type] : '';
    return `This organisation exceeds the ${typeName} limit for ${overItems.join(' and ')}. Usage must be reduced before switching to this licence type.`;
  });

  readonly licenceTypeField = new FormBase({
    key: 'licenceType',
    label: 'Licence Type',
    type: 'dropdown',
    placeholder: 'Select a licence type',
    disabled: false,
    required: true,
    value: '',
    options: licenseTypeOptions(),
  });

  readonly licenceTypeForm = this.fb.group({
    licenceType: ['', Validators.required],
  });

  constructor() {
    this.element = this.elementRef.nativeElement;
  }

  ngOnInit(): void {
    this.element.addEventListener('click', (e: Event) => {
      const target = e.target as HTMLElement;
      if (target.className === 'modal--container') {
        this.close();
      }
    });

    this.licenceTypeForm.controls.licenceType.valueChanges
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((value) => {
        const parsed = Number(value);
        this.selectedLicenceType.set(isLicenseType(parsed) ? parsed : null);
      });

    this.modalService.add(this);
    document.body.appendChild(this.element);
  }

  open(): void {
    const type = this.currentLicenceType();
    if (type !== undefined) {
      this.licenceTypeForm.patchValue({ licenceType: String(type) });
      this.selectedLicenceType.set(type);
    }
    this.element.style.display = 'block';
  }

  close(): void {
    this.element.style.display = 'none';
  }

  onSubmit(): void {
    const selected = this.selectedLicenceType();
    if (selected === null || this.isOverLimit() || this.processing()) return;
    this.licenceTypeConfirmed.emit(selected);
    // The parent closes this modal on a successful update; keeping it open
    // during the request preserves the user's selection if it fails.
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
    this.modalService.remove(this.id());
    this.element.remove();
  }
}
